import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { knowledgeBaseService, KnowledgeArticleRating, CreateKnowledgeArticleRatingRequest } from '../services/knowledgeBaseService';
import { Button } from './Button';

interface ArticleRatingProps {
  articleId: string;
  currentRating: number;
  ratingCount: number;
  onRatingUpdate?: (newRating: number, newCount: number) => void;
  showReviews?: boolean;
  className?: string;
}

export const ArticleRating: React.FC<ArticleRatingProps> = ({
  articleId,
  currentRating,
  ratingCount,
  onRatingUpdate,
  showReviews = true,
  className = ''
}) => {
  const { user } = useAuth();
  const [userRating, setUserRating] = useState<KnowledgeArticleRating | null>(null);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [isRating, setIsRating] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingComment, setRatingComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [allRatings, setAllRatings] = useState<KnowledgeArticleRating[]>([]);
  const [loadingRatings, setLoadingRatings] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserRating();
    }
  }, [articleId, user]);

  useEffect(() => {
    if (showReviews) {
      loadAllRatings();
    }
  }, [articleId, showReviews]);

  const loadUserRating = async () => {
    if (!user) return;

    try {
      const rating = await knowledgeBaseService.getUserArticleRating(articleId);
      setUserRating(rating);
      if (rating) {
        setRatingComment(rating.comment || '');
      }
    } catch (err) {
      console.error('Error loading user rating:', err);
    }
  };

  const loadAllRatings = async () => {
    try {
      setLoadingRatings(true);
      const ratings = await knowledgeBaseService.getArticleRatings(articleId, 1, 10);
      setAllRatings(ratings);
    } catch (err) {
      console.error('Error loading ratings:', err);
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleStarClick = async (rating: number) => {
    if (!user) {
      setError('Please log in to rate articles');
      return;
    }

    try {
      setIsRating(true);
      setError(null);

      const ratingRequest: CreateKnowledgeArticleRatingRequest = {
        articleId,
        rating,
        comment: ratingComment
      };

      const updatedRating = await knowledgeBaseService.rateArticle(articleId, ratingRequest);
      setUserRating(updatedRating);
      
      // Update parent component if callback provided
      if (onRatingUpdate) {
        // For simplicity, just increment count if it's a new rating
        const newCount = userRating ? ratingCount : ratingCount + 1;
        onRatingUpdate(rating, newCount);
      }

      setShowRatingForm(false);
      
      // Reload all ratings to show the updated list
      if (showReviews) {
        await loadAllRatings();
      }
    } catch (err: any) {
      console.error('Error rating article:', err);
      setError(err.response?.data?.message || 'Failed to rate article');
    } finally {
      setIsRating(false);
    }
  };

  const handleStarHover = (star: number | null) => {
    setHoveredStar(star);
  };

  const renderStars = (isInteractive = false, size = 'medium') => {
    const sizeClasses = {
      small: 'w-4 h-4',
      medium: 'w-5 h-5',
      large: 'w-6 h-6'
    };

    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isActive = isInteractive 
        ? (hoveredStar ? i <= hoveredStar : userRating ? i <= userRating.rating : false)
        : i <= Math.round(currentRating);

      stars.push(
        <button
          key={i}
          type="button"
          // A star with no name is announced only as "button". The rating it applies is the
          // whole point of the control, so it has to be in the name.
          aria-label={isInteractive ? `Rate ${i} out of 5` : `Rated ${i} out of 5`}
          aria-pressed={isInteractive ? isActive : undefined}
          disabled={!isInteractive || isRating}
          onClick={() => isInteractive && handleStarClick(i)}
          onMouseEnter={() => isInteractive && handleStarHover(i)}
          onMouseLeave={() => isInteractive && handleStarHover(null)}
          className={`${sizeClasses[size]} ${isInteractive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-all duration-200 ${
            isActive 
              ? 'text-yellow-400' 
              : 'text-gray-300'
          } ${isRating ? 'opacity-50' : ''}`}
        >
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      );
    }
    return stars;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Overall Rating Display */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-1">
          {renderStars(false, 'medium')}
        </div>
        <div className="text-sm text-gray-600">
          {currentRating > 0 ? (
            <span>
              {currentRating.toFixed(1)} out of 5 ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
            </span>
          ) : (
            <span>No ratings yet</span>
          )}
        </div>
      </div>

      {/* User Rating Section */}
      {user && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {userRating ? 'Your Rating' : 'Rate this article'}
          </h4>
          
          <div className="flex items-center space-x-2 mb-3">
            {renderStars(true, 'large')}
            {userRating && (
              <span className="text-sm text-gray-600 ml-2">
                You rated this {userRating.rating} star{userRating.rating !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {(showRatingForm || userRating?.comment) && (
            <div className="mt-3">
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Add a comment (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={3}
                disabled={isRating}
              />
              {showRatingForm && (
                <div className="flex items-center justify-end space-x-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowRatingForm(false);
                      setRatingComment(userRating?.comment || '');
                    }}
                    disabled={isRating}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleStarClick(userRating?.rating || 5)}
                    disabled={isRating}
                  >
                    {isRating ? 'Saving...' : 'Update Rating'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {userRating && !showRatingForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRatingForm(true)}
              className="mt-2"
            >
              Edit Rating
            </Button>
          )}

          {error && (
            <p className="text-red-600 text-sm mt-2">{error}</p>
          )}
        </div>
      )}

      {/* All Ratings/Reviews */}
      {showReviews && allRatings.length > 0 && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Recent Reviews ({allRatings.length})
          </h4>
          
          {loadingRatings ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {allRatings.slice(0, 5).map((rating) => (
                <div key={rating.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {rating.userName}
                      </span>
                      <div className="flex items-center space-x-1">
                        {renderStars(false, 'small')}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatTimeAgo(rating.ratedAt)}
                    </span>
                  </div>
                  {rating.comment && (
                    <p className="text-sm text-gray-700">{rating.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleRating;